import React, { useEffect, useContext, useState } from 'react'
import { ProjectContext } from 'contexts/ProjectContext'
import Icon from 'components/Icon'
import { Button } from '@arnsbogroup/react-voicearchive'
import { FormattedMessage, useIntl } from 'react-intl'
import messages from './messages'
import categoryMessages from 'data/categories/messages'
import languageMessages from 'data/languages/messages'
import { track } from 'tracking'
import { messages as routeMessages } from 'routes'
import { Link, useLocation } from 'react-router-dom'
import Waveform from 'components/Waveform'
import { MediaPlayerContext } from 'contexts/PlayerContext'
import { useKeyboardShortcut } from '@arnsbogroup/react-voicearchive/lib/hooks'
import classNames from 'classnames'

const Timer = () => {
  const { player } = useContext(MediaPlayerContext)

  const asSecString = (seconds) => {
    let secs = parseInt(seconds, 10)
    let mins = Math.floor(secs / 60)

    secs = secs - mins * 60

    if (mins < 10) {
      mins = '0' + mins
    }

    if (secs < 10) {
      secs = '0' + secs
    }

    return mins + ':' + secs
  }

  const [time, setTime] = useState(asSecString(0) + '/' + asSecString(0))
  useEffect(() => {
    const interval = setInterval(() => {
      if (player._src === '') {
        setTime('00:00/00:00')
      } else {
        setTime(
          asSecString(player.seek()) + ' / ' + asSecString(player.duration())
        )
      }
    }, 100)
    return () => {
      clearInterval(interval)
    }
  }, [player])

  return <span>{time}</span>
}

export const Player = (props) => {
  const { formatMessage } = useIntl()
  const location = useLocation()
  const { project, dispatch } = useContext(ProjectContext)
  const { player, playing, meta, loadMeta } = useContext(MediaPlayerContext)

  useKeyboardShortcut(
    [' '],
    () => {
      if (player) {
        playing ? player.pause() : player.play()
      }
    },
    { overrideSystem: true }
  )

  useKeyboardShortcut(['Escape'], () => loadMeta(null), {
    overrideSystem: true,
  })

  if (!player || !meta?.sku) {
    return <div />
  }

  const onAdd = () => {
    if (
      project.voices[meta.sku] &&
      project.voices[meta.sku].demos.findIndex(({ id }) => id === meta.id) > -1
    ) {
      return
    }
  }

  return (
    <div
      className={classNames(
        'hidden md:block z-50 fixed inset-x-0 bottom-0 h-24 md:h-20 px-10 md:px-6 bg-white border-t transition-[width] ease-in-out delay-150',
        {
          hidden: !player,
          'md:w-3/4': project.isScoped && location.pathname.includes(formatMessage(routeMessages.explore).replace('/*', '')),
          'w-full': !project.isScoped || !location.pathname.includes(formatMessage(routeMessages.explore).replace('/*', ''))
        }
      )}
    >
      <div className="container flex items-center justify-between m-auto h-full space-x-4">
        {playing ? (
          <Icon
            icon={['fas', 'pause']}
            className="cursor-pointer text-2xl text-primary"
            onClick={() =>
              player.pause() &&
              track('Player.pause.click', {
                sku: meta.sku,
                demo: meta.id,
              })
            }
          />
        ) : (
          <Icon
            icon={['fas', 'play']}
            className="cursor-pointer text-2xl text-primary"
            onClick={() =>
              player.play() &&
              track('Player.play.click', {
                sku: meta.sku,
                demo: meta.id,
              })
            }
          />
        )}

        <div className="font-medium text-sm text-gray-700 truncate">
          <span>
            <Link to={`/${meta.sku}`}>
              <div className="text-primary hover:text-primary-darker cursor-pointer mr-1">
                { meta.sku }
              </div>
            </Link>

            <div className="capitalize">
              {[
                meta.voiceage,
                meta.product in categoryMessages
                  ? formatMessage(categoryMessages[meta.product])
                  : meta.productName,
                meta.language in languageMessages
                  ? formatMessage(languageMessages[meta.language])
                  : meta.language,
              ]
                .filter(Boolean)
                .join(', ')}
            </div>
          </span>
        </div>

        {player && (
          <div className="hidden md:flex flex-grow items-center">
            <Waveform src={player._src} height={48} />
          </div>
        )}

        <div className="font-medium text-sm text-gray-700 mr-2 text-right whitespace-no-wrap">
          <Timer />
        </div>
      </div>

      <Icon
        icon="times"
        className="cursor-pointer absolute top-0 right-0 mt-2 mr-2"
        onClick={() =>
          loadMeta(null) && player.pause() && track('Player.close.click')
        }
      />
    </div>
  )
}

export default Player
